import React from "react";
import { css } from "@emotion/css";
import type { SessionPlan } from "../../types/state";

/**
 * Phase progress strip shown at the TOP of a coaching session.
 *
 *   Hook  ->  Frame  ->  Build  ->  Prove  ->  Wrap
 *
 * Mirrors the onboarding ``OnboardingProgress`` affordance: color encodes
 * PROGRESS STATE (completed chips are green, the current chip is the
 * brand primary, upcoming chips are muted), so the eye reads "fill from
 * the left" as the learner advances through the fixed number of steps.
 *
 * ``currentIndex`` is the 0-based index of the phase the learner is in
 * right now (intro = 0, each lesson = +1, the wrap overlay = last).  A
 * compact "Step N of M" counter sits to the right.
 *
 * ``remainingWords`` replaces that counter where the server sends one.
 * On the runtime engine the crafter says how much is left IN WORDS ("one
 * more after this", "a good stretch ahead") — the display law, and also
 * the honest shape of a sitting a recipe may end early: a digit counter
 * promises a length nobody agreed to.
 */

interface SessionProgressProps {
  plan: SessionPlan;
  /** 0-based index into ``plan.phases`` for the current phase. */
  currentIndex: number;
  /** When true (wrap overlay open / session done), all content phases
   *  read as completed and the terminal Wrap phase is current. */
  atWrap?: boolean;
  /** The server's own words for how much is left.  Replaces the digit
   *  counter entirely where present. */
  remainingWords?: string;
}

const NARROW_BREAKPOINT = "@media (max-width: 480px)";

const wrapperStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--color-bg-subtle);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const chipsRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
  font-size: 0.78em;

  ${NARROW_BREAKPOINT} {
    gap: 4px;
    font-size: 0.68em;
  }
`;

const nodeBaseStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  ${NARROW_BREAKPOINT} {
    padding: 3px 7px;
    gap: 4px;
  }
`;

const nodeCompletedStyle = css`
  background: var(--color-success-bg);
  color: var(--color-success-text);
  border: 1px solid var(--color-success-border);
`;

const nodeCurrentStyle = css`
  background: var(--color-primary-muted);
  color: var(--color-primary);
  border: 1px solid var(--color-primary);
  font-weight: 600;
`;

const nodeUpcomingStyle = css`
  background: var(--color-bg-raised);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border-subtle);
`;

const separatorStyle = css`
  color: var(--color-text-muted);
  user-select: none;

  ${NARROW_BREAKPOINT} {
    display: none;
  }
`;

const prefixStyle = css`
  ${NARROW_BREAKPOINT} {
    display: none;
  }
`;

const counterStyle = css`
  font-size: 0.75em;
  font-weight: 600;
  color: var(--color-text-muted);
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const SessionProgress: React.FC<SessionProgressProps> = ({
  plan,
  currentIndex,
  atWrap = false,
  remainingWords,
}) => {
  const phases = plan.phases ?? [];
  if (phases.length === 0) return null;

  // The terminal phase (Wrap) is the last entry; content phases precede it.
  const lastIndex = phases.length - 1;
  const effectiveIndex = atWrap ? lastIndex : Math.min(currentIndex, lastIndex);

  // "Step N of M" counts content steps only (excludes the wrap entry).
  const totalSteps = plan.totalSteps || lastIndex;
  const stepNumber = Math.min(effectiveIndex + 1, totalSteps);

  return (
    <div className={wrapperStyle} data-testid="session-progress">
      <div className={chipsRowStyle}>
        {phases.map((node, idx) => {
          const completed = idx < effectiveIndex;
          const current = idx === effectiveIndex;
          const state: "completed" | "current" | "upcoming" = completed
            ? "completed"
            : current
              ? "current"
              : "upcoming";
          const stateStyle = completed
            ? nodeCompletedStyle
            : current
              ? nodeCurrentStyle
              : nodeUpcomingStyle;
          return (
            <React.Fragment key={node.id}>
              <span
                className={`${nodeBaseStyle} ${stateStyle}`}
                data-state={state}
                data-testid={`session-progress-step-${node.id}`}
              >
                {(completed || current) && (
                  <span className={prefixStyle} aria-hidden="true">
                    {completed ? "✓" : "→"}
                  </span>
                )}
                {node.label}
              </span>
              {idx < phases.length - 1 && (
                <span className={separatorStyle} aria-hidden="true">·</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
      {!atWrap && remainingWords && (
        <div className={counterStyle} data-testid="session-progress-remaining">
          {remainingWords}
        </div>
      )}
      {!atWrap && !remainingWords && totalSteps > 0 && (
        <div className={counterStyle} data-testid="session-progress-counter">
          Step {stepNumber} of {totalSteps}
        </div>
      )}
    </div>
  );
};
